'use client';

import Chart from 'chart.js/auto';
import React, { useEffect, useRef } from 'react';
import { getResolvedColorValue } from 'src/utils/theme/colorVars';

const MiniChart = ({
	useMinMaxTicks = true,
	maxHeight = 50,
	...props
}: {
	data: number[];
	useMinMaxTicks?: boolean;
	isPositive?: boolean;
	/**
	 * Defaults to `50`.
	 */
	maxHeight?: number;
	fixedWidth?: number;
	useNeutralColor?: boolean;
}) => {
	const chartHolderRef = React.createRef<HTMLCanvasElement>();
	const chartRef = useRef<Chart>(null);

	const BUFFER = 0.07;

	let dataToUse = props.data;

	if (dataToUse.length === 1) {
		dataToUse = [0, ...dataToUse];
	}

	// min/max can be distorted for markets with very small unit sizes
	dataToUse = dataToUse.map((point) => point * 100);

	useEffect(() => {
		if (!dataToUse || dataToUse?.length == 0) return;

		const max = Math.max(...dataToUse);
		const min = Math.min(...dataToUse);

		const maxIsNegative = max < 0;
		const minIsNegative = min < 0;

		const maxTick = max * (maxIsNegative ? 1 - BUFFER : 1 + BUFFER);
		const minTick = min * (minIsNegative ? 1 + BUFFER : 1 - BUFFER);

		const chartData = {
			datasets: [{ data: dataToUse }],
			labels: dataToUse.map(() => ''),
		};

		const chartElement = chartHolderRef.current;
		const greenGradient = chartElement
			?.getContext('2d')
			?.createLinearGradient(0, 0, 0, maxHeight);

		greenGradient?.addColorStop(0, '#2BA8664D');
		greenGradient?.addColorStop(1, 'rgba(0,0,0,0)');

		const redGradient = chartElement
			?.getContext('2d')
			?.createLinearGradient(0, 0, 0, maxHeight);

		redGradient?.addColorStop(0, '#E2563D4D');
		redGradient?.addColorStop(1, 'rgba(0,0,0,0)');

		const neutralGradient = chartElement
			?.getContext('2d')
			?.createLinearGradient(0, 0, 0, maxHeight);
		neutralGradient?.addColorStop(0, '#742A814D');
		neutralGradient?.addColorStop(1, '#9D66CE00');

		const { borderColor, aboveColor } = (() => {
			if (props.useNeutralColor) {
				return {
					borderColor: getResolvedColorValue('button-primary-bg-hover'),
					aboveColor: neutralGradient,
				};
			}

			if (props.isPositive) {
				return {
					borderColor: getResolvedColorValue('text-positive-green-button'),
					aboveColor: greenGradient,
				};
			}

			return {
				borderColor: getResolvedColorValue('text-negative-red-button'),
				aboveColor: redGradient,
			};
		})();

		const chart = new Chart(chartElement as HTMLCanvasElement, {
			type: 'line',
			data: chartData,
			options: {
				layout: {
					padding: {
						right: 20,
						left: 0,
					},
				},
				animation: {
					duration: 0,
				},
				plugins: {
					legend: { display: false },
					tooltip: {
						enabled: false,
					},
				},
				elements: {
					point: {
						pointStyle: 'circle',
						borderWidth: 0,
						hoverRadius: 0,
						backgroundColor: 'rgba(0, 0, 0, 0)',
					},
					line: {
						borderWidth: 1,
						fill: {
							target: 'origin',
							above: aboveColor,
						},
					},
				},
				borderColor,
				maintainAspectRatio: false,
				scales: {
					x: {
						ticks: {
							display: false,
						},
						grid: {
							display: false,
							drawBorder: false,
						},
					},
					y: {
						beginAtZero: true,
						type: 'linear',
						min: useMinMaxTicks ? (minTick == 0 ? -1 : minTick) : undefined,
						max: useMinMaxTicks ? (maxTick == 0 ? 1 : maxTick) : undefined,
						ticks: {
							display: false,
							maxTicksLimit: 3,
						},
						grid: {
							display: false,
							drawBorder: false,
						},
					},
				},
			},
		});

		chartRef.current = chart;
		return () => {
			chartRef.current?.destroy();
		};
	}, [props.data]);

	return (
		<canvas
			style={{
				maxHeight,
				maxWidth: props.fixedWidth,
				minWidth: props.fixedWidth,
			}}
			ref={chartHolderRef}
		/>
	);
};

export default React.memo(MiniChart);
